import { useEffect, useState } from "react";
import { server } from "../../server";
import Select from "react-select";
import axios from "axios";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { getSession } from "../../functions";
import BusSeatView from "../../components/BusSeatView";
import * as XLSX from "xlsx";
import {
  bloodGroupList,
  categoryList,
  certificatesList,
  classList,
  monthsList,
  relationshipsList,
  religionList,
  sectionList,
  statesList,
  subjectList
} from "../../utils";

const getSessionOptions = () => {
  const currentSession = getSession();
  if (!currentSession || typeof currentSession !== "string") return [];

  const match = currentSession.match(/^(\d{4})-(\d{2}|\d{4})$/);
  if (!match) {
    return [{ label: currentSession, value: currentSession }];
  }

  const startYear = parseInt(match[1], 10);
  const endPart = match[2];
  const isShortForm = endPart.length === 2;

  const nextStartYear = startYear + 1;
  const nextEndYear = nextStartYear + 1;
  const nextEndPart = isShortForm
    ? String(nextEndYear % 100).padStart(2, "0")
    : String(nextEndYear);

  const nextSession = `${nextStartYear}-${nextEndPart}`;

  return [
    { label: currentSession, value: currentSession },
    { label: nextSession, value: nextSession },
  ];
};

const SENIOR_SECONDARY_CLASSES = ["XI", "XII"];

const isSeniorSecondaryClass = (cls) => {
  if (!cls) return false;
  return SENIOR_SECONDARY_CLASSES.includes(cls.toString().trim().toUpperCase());
};

const STREAM_OPTIONS = ["Science", "Commerce", "Humanities"].map((stream) => ({
  label: stream,
  value: stream,
}));

const SUBJECT_COMBINATIONS = {
  Science: {
    compulsory: ["English", "Physics", "Chemistry"],
    optionalGroups: [
      { label: "Group 1", options: ["Mathematics", "Biology"] },
      {
        label: "Group 2",
        options: ["Informatics Practices", "Physical Education"],
      },
    ],
  },
  Commerce: {
    compulsory: ["English", "Business Studies", "Accountancy", "Economics"],
    optionalGroups: [
      {
        label: "Optional",
        options: ["Mathematics", "Physical Education", "Informatics Practices"],
      },
    ],
  },
  Humanities: {
    compulsory: ["History", "English", "Psychology", "Economics"],
    optionalGroups: [
      {
        label: "Optional",
        options: ["Mathematics", "Physical Education", "Informatics Practices"],
      },
    ],
  },
};

const getOptionalSubjectGroups = (stream) => {
  const combo = SUBJECT_COMBINATIONS[stream];
  if (!combo) return [];
  return combo.optionalGroups.map((group) => ({
    label: group.label,
    options: group.options.map((subject) => ({
      label: subject,
      value: subject,
    })),
  }));
};

function AddStudent() {
  const generateStudentId = () => {
    const randomDigits = Math.floor(10000 + Math.random() * 90000);
    return `AHR${randomDigits}`;
  };

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [isSameAddress, setIsSameAddress] = useState(false);
  const [transport, setTransport] = useState([]);
  const [siblingAdmissionNos, setSiblingAdmissionNos] = useState([]);
  const [staffId, setStaffId] = useState("");
  const [newSiblingAdmissionNo, setNewSiblingAdmissionNo] = useState("");
  const [houseCount, setHouseCount] = useState({});
  const [selectedHouse, setSelectedHouse] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [sessionOptions] = useState(() => getSessionOptions());
  const [subjectSelections, setSubjectSelections] = useState(
    Array(8).fill(""),
  );

  const initialState = {
    currentClass: "",
    session: getSession() || "",
    stream: "",
    studentDetails: {
      studentId: generateStudentId(),
      admissionDate: new Date().toISOString().split("T")[0],
      name: "",
      admissionInClass: "",
      admissionInSection: "",
      currentClass: "",
      currentSection: "",
      stream: "",
      subjects: [],
      rollNo: "",
      gender: "",
      dateOfBirth: "",
      category: "",
      house: "",
      aadhar_no: "",
      apaarId: "",
      pen: "",
      accountNo: "",
      religion: "",
      bloodGroup: "",
      handicap: "",
      previousSchoolName: "",
      previousSchoolAddress: "",
      underMidSection: "",
      applicableMonth: "",
      phoneNo: "",
      studentImage: "",
    },
    parentDetails: [
      {
        name: "",
        qualification: "",
        occupation: "",
        designation: "",
        phone: "",
        aadhaarNo: "",
        email: "",
        Income: "",
        relation: "Father",
      },
      {
        name: "",
        qualification: "",
        occupation: "",
        designation: "",
        phone: "",
        aadhaarNo: "",
        email: "",
        Income: "",
        relation: "Mother",
      },
      {
        name: "",
        phone: "",
        aadhaarNo: "",
        relation: "Guardian",
      },
      {
        name: "",
        phone: "",
        aadhaarNo: "",
        relation: "Emergency Guardian",
      },
    ],
    presentAddress: {
      address: "",
      city: "",
      pincode: "",
      state: "",
    },
    permanentAddress: {
      address: "",
      city: "",
      pincode: "",
      state: "",
    },
    transportFacility: {},
    certificate: [],
    subjects: [],
    scholarship: {
      type: "",
      siblingAdmissionNo: [],
      staffId: "",
      scholarshipFrom: "",
      discount: {
        admissionFee: 0,
        monthlyFee: 0,
        developmentFee: 0,
      },
    },
  };

  const [formData, setFormData] = useState(initialState);

  // ── Excel Download Template ────────────────────────────────────────────────
  const handleDownloadTemplate = () => {
    const wb = XLSX.utils.book_new();

    // ── Sheet 1: Student Details ─────────────────────────────────────────────
    const studentRows = [
      ["STUDENT ADMISSION FORM — Fill all sheets then upload"],
      ["Field", "Value", "Field", "Value"],
      ["Session *", "", "Student ID (auto-generated)", ""],
      ["Admission Date * (YYYY-MM-DD)", "", "Full Name *", ""],
      ["Admission in Class *", "", "Admission in Section *", ""],
      ["Current Class *", "", "Current Section *", ""],
      ["Roll No *", "", "Gender * (Male/Female)", ""],
      ["Date of Birth * (YYYY-MM-DD)", "", "Category *", ""],
      ["House (auto-assigned)", "", "Blood Group *", ""],
      ["Aadhaar No * (12 digits)", "", "Apaar ID * (12 digits)", ""],
      ["PEN * (12 digits)", "", "Bank Account No *", ""],
      ["Religion *", "", "Handicap * (Yes/No)", ""],
      ["Previous School Name *", "", "Previous School Address *", ""],
      ["Under Mid Session * (Yes/No)", "", "Applicable Month *", ""],
      ["Primary Phone No * (10 digits)", "", "", ""],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(studentRows);
    ws1["!cols"] = [{ wch: 35 }, { wch: 35 }, { wch: 35 }, { wch: 35 }];
    XLSX.utils.book_append_sheet(wb, ws1, "Student Details");

    // ── Sheet 2: Parent Details ──────────────────────────────────────────────
    const parentRows = [
      ["PARENT DETAILS — Fill all sections"],
      [
        "Father",
        "",
        "",
        "Mother",
        "",
        "",
        "Guardian",
        "",
        "",
        "Emergency Guardian",
        "",
      ],
      [
        "Field",
        "Value",
        "",
        "Field",
        "Value",
        "",
        "Field",
        "Value",
        "",
        "Field",
        "Value",
      ],
      ["Name", "", "", "Name", "", "", "Name", "", "", "Name", ""],
      [
        "Qualification",
        "",
        "",
        "Qualification",
        "",
        "",
        "Phone (10 digits)",
        "",
        "",
        "Phone (10 digits)",
        "",
      ],
      [
        "Occupation",
        "",
        "",
        "Occupation",
        "",
        "",
        "Aadhaar No (12 digits)",
        "",
        "",
        "Aadhaar No (12 digits)",
        "",
      ],
      ["Designation", "", "", "Designation", "", "", "", "", "", "", ""],
      [
        "Phone (10 digits)",
        "",
        "",
        "Phone (10 digits)",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      [
        "Aadhaar No (12 digits)",
        "",
        "",
        "Aadhaar No (12 digits)",
        "",
        "",
        "",
        "",
        "",
        "",
        "",
      ],
      ["Email", "", "", "Email", "", "", "", "", "", "", ""],
      ["Income", "", "", "Income", "", "", "", "", "", "", ""],
    ];
    const ws2 = XLSX.utils.aoa_to_sheet(parentRows);
    ws2["!cols"] = Array(11).fill({ wch: 28 });
    XLSX.utils.book_append_sheet(wb, ws2, "Parent Details");

    // ── Sheet 3: Address ─────────────────────────────────────────────────────
    const addressRows = [
      ["ADDRESS DETAILS"],
      ["Present Address", "", "Permanent Address", ""],
      ["Field", "Value", "Field", "Value"],
      ["Address *", "", "Address *", ""],
      ["City *", "", "City *", ""],
      ["Pincode *", "", "Pincode *", ""],
      ["State *", "", "State *", ""],
    ];
    const ws3 = XLSX.utils.aoa_to_sheet(addressRows);
    ws3["!cols"] = [{ wch: 32 }, { wch: 35 }, { wch: 32 }, { wch: 35 }];
    XLSX.utils.book_append_sheet(wb, ws3, "Address");

    // ── Sheet 4: Transport & Scholarship ─────────────────────────────────────
    const tsRows = [
      ["TRANSPORT & SCHOLARSHIP — Fill only if applicable"],
      ["--- TRANSPORT FACILITY ---"],
      ["Field", "Value", "Field", "Value"],
      ["Bus Stoppage", "", "Vehicle No.", ""],
      ["Bus No.", "", "Applicable Month", ""],
      [""],
      ["--- SCHOLARSHIP ---"],
      ["Field", "Value", "Field", "Value"],
      ["Ward Type (sibling/staff/other)", "", "Scholarship From Month", ""],
      ["Sibling Admission No(s) (comma sep.)", "", "Staff ID", ""],
      ["Admission Fee Discount", "", "Monthly Fee Discount", ""],
      ["Development Fee Discount", "", "", ""],
    ];
    const ws4 = XLSX.utils.aoa_to_sheet(tsRows);
    ws4["!cols"] = [{ wch: 38 }, { wch: 35 }, { wch: 38 }, { wch: 35 }];
    XLSX.utils.book_append_sheet(wb, ws4, "Transport & Scholarship");

    // ── Sheet 5: Instructions ────────────────────────────────────────────────
    const instrRows = [
      ["HOW TO USE THIS TEMPLATE"],
      ["Step", "Instruction"],
      [
        "Step 1",
        "Fill all 4 data sheets: Student Details, Parent Details, Address, Transport & Scholarship",
      ],
      [
        "Step 2",
        "Fields marked * are required. Leave optional fields blank if not applicable.",
      ],
      ["Step 3", "Do NOT rename sheets or change column headers."],
      ["Step 4", "Do NOT add extra rows or columns."],
      ["Step 5", "Save as .xlsx before uploading."],
      [
        "Step 6",
        "Click 'Upload Excel' in the form — all fields will auto-populate and remain editable.",
      ],
      ["Step 7", "Review all fields, then click Submit."],
      ["Note", "Student ID is auto-generated — leave it blank."],
      ["Note", "Bus No. is auto-filled from stoppage + vehicle number."],
      [
        "Note",
        "For multiple sibling admission numbers, separate with commas e.g. 1001,1002",
      ],
      ["Note", "Date format must be YYYY-MM-DD e.g. 2010-05-15"],
    ];
    const ws5 = XLSX.utils.aoa_to_sheet(instrRows);
    ws5["!cols"] = [{ wch: 12 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(wb, ws5, "Instructions");

    XLSX.writeFile(wb, "student_admission_template.xlsx");
  };

  // ── Excel Upload → Parse → Fill Form ─────────────────────────────────────
  const handleUploadExcel = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, { type: "binary" });

        // Helper: sheet rows as array of arrays
        const getRows = (sheetName) => {
          const ws = workbook.Sheets[sheetName];
          if (!ws) return [];
          return XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });
        };

        // Helper: build label→value map from col pairs (colA, colB)
        const buildMap = (rows, labelCol, valueCol) => {
          const map = {};
          rows.forEach((row) => {
            const label = String(row[labelCol] || "").trim();
            const value = String(row[valueCol] || "").trim();
            if (label) map[label] = value;
          });
          return map;
        };

        // ── Student Details ──────────────────────────────────────────────────
        const sdRows = getRows("Student Details");
        const sdA = buildMap(sdRows, 0, 1); // cols A+B
        const sdC = buildMap(sdRows, 2, 3); // cols C+D
        const sd = { ...sdA, ...sdC };

        const studentPatch = {
          admissionDate:
            sd["Admission Date * (YYYY-MM-DD)"] ||
            formData.studentDetails.admissionDate,
          name: sd["Full Name *"] || "",
          admissionInClass: sd["Admission in Class *"] || "",
          admissionInSection: sd["Admission in Section *"] || "",
          currentClass: sd["Current Class *"] || "",
          currentSection: sd["Current Section *"] || "",
          rollNo: sd["Roll No *"] || "",
          gender: sd["Gender * (Male/Female)"] || "",
          dateOfBirth: sd["Date of Birth * (YYYY-MM-DD)"] || "",
          category: sd["Category *"] || "",
          bloodGroup: sd["Blood Group *"] || "",
          aadhar_no: sd["Aadhaar No * (12 digits)"] || "",
          apaarId: sd["Apaar ID * (12 digits)"] || "",
          pen: sd["PEN * (12 digits)"] || "",
          accountNo: sd["Bank Account No *"] || "",
          religion: sd["Religion *"] || "",
          handicap: sd["Handicap * (Yes/No)"] || "",
          previousSchoolName: sd["Previous School Name *"] || "",
          previousSchoolAddress: sd["Previous School Address *"] || "",
          underMidSection: sd["Under Mid Session * (Yes/No)"] || "",
          applicableMonth: sd["Applicable Month *"] || "",
          phoneNo: sd["Primary Phone No * (10 digits)"] || "",
        };

        const sessionVal = sd["Session *"] || "";
        const currentClassVal = studentPatch.currentClass;

        // ── Parent Details ───────────────────────────────────────────────────
        // Layout: Father=cols0+1, Mother=cols3+4, Guardian=cols6+7, EmergGuardian=cols9+10
        const pdRows = getRows("Parent Details");
        const colOffsets = [0, 3, 6, 9];

        const parentKeyMap = {
          Name: "name",
          Qualification: "qualification",
          Occupation: "occupation",
          Designation: "designation",
          "Phone (10 digits)": "phone",
          "Aadhaar No (12 digits)": "aadhaarNo",
          Email: "email",
          Income: "Income",
        };

        const newParentDetails = formData.parentDetails.map((parent, idx) => {
          const offset = colOffsets[idx];
          const patch = { ...parent };
          pdRows.forEach((row) => {
            const label = String(row[offset] || "").trim();
            const val = String(row[offset + 1] || "").trim();
            if (label && parentKeyMap[label] && val) {
              patch[parentKeyMap[label]] = val;
            }
          });
          return patch;
        });

        // ── Address ──────────────────────────────────────────────────────────
        const addrRows = getRows("Address");
        const presentMap = buildMap(addrRows, 0, 1);
        const permanentMap = buildMap(addrRows, 2, 3);

        const presentPatch = {
          address: presentMap["Address *"] || "",
          city: presentMap["City *"] || "",
          pincode: presentMap["Pincode *"] || "",
          state: presentMap["State *"] || "",
        };
        const permanentPatch = {
          address: permanentMap["Address *"] || "",
          city: permanentMap["City *"] || "",
          pincode: permanentMap["Pincode *"] || "",
          state: permanentMap["State *"] || "",
        };

        // ── Transport & Scholarship ──────────────────────────────────────────
        const tsRows = getRows("Transport & Scholarship");
        const tsA = buildMap(tsRows, 0, 1);
        const tsC = buildMap(tsRows, 2, 3);
        const ts = { ...tsA, ...tsC };

        const transportPatch = {
          busStop: ts["Bus Stoppage"] || "",
          vehicleNo: ts["Vehicle No."] || "",
          busNumber: ts["Bus No."] || "",
          applicableMonth: ts["Applicable Month"] || "",
        };

        const rawSiblings = ts["Sibling Admission No(s) (comma sep.)"] || "";
        const siblingList = rawSiblings
          ? rawSiblings
              .split(",")
              .map((s) => s.trim())
              .filter(Boolean)
          : [];

        const scholarshipPatch = {
          type: ts["Ward Type (sibling/staff/other)"] || "",
          scholarshipFrom: ts["Scholarship From Month"] || "",
          siblingAdmissionNo: siblingList,
          staffId: ts["Staff ID"] || "",
          discount: {
            admissionFee: ts["Admission Fee Discount"] || 0,
            monthlyFee: ts["Monthly Fee Discount"] || 0,
            developmentFee: ts["Development Fee Discount"] || 0,
          },
        };

        if (scholarshipPatch.staffId) setStaffId(scholarshipPatch.staffId);

        // ── Commit all changes ───────────────────────────────────────────────
        setFormData((prev) => ({
          ...prev,
          session: sessionVal || prev.session,
          currentClass: currentClassVal || prev.currentClass,
          studentDetails: {
            ...prev.studentDetails,
            ...studentPatch,
          },
          parentDetails: newParentDetails,
          presentAddress: presentPatch,
          permanentAddress: permanentPatch,
          transportFacility: transportPatch,
          scholarship: scholarshipPatch,
        }));

        toast.success("Excel data loaded! Review and submit.", {
          position: "top-right",
        });
      } catch (err) {
        console.error("Excel parse error:", err);
        toast.error("Failed to read Excel file. Please use the template.", {
          position: "top-right",
        });
      }
    };
    reader.readAsBinaryString(file);
    e.target.value = ""; // allow re-upload of same file
  };

  // ── Existing handlers (unchanged) ─────────────────────────────────────────

  const handleStudentDetailsChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      studentDetails: {
        ...prev.studentDetails,
        [name]: value,
      },
    }));
  };

  const handleParentDetailsChange = (index, e) => {
    const { name, value } = e.target;
    setFormData((prevForm) => {
      const updatedParentDetails = [...prevForm.parentDetails];
      updatedParentDetails[index] = {
        ...updatedParentDetails[index],
        [name]: value,
      };
      return { ...prevForm, parentDetails: updatedParentDetails };
    });
  };

  const handlePresentAddressChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const updatedPresentAddress = {
        ...prev.presentAddress,
        [name]: value,
      };

      const updatedPermanentAddress = isSameAddress
        ? { ...updatedPresentAddress }
        : prev.permanentAddress;

      return {
        ...prev,
        presentAddress: updatedPresentAddress,
        permanentAddress: updatedPermanentAddress,
      };
    });
  };

  const handlePermanentAddressChange = (e) => {
    if (isSameAddress) return;

    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      permanentAddress: {
        ...prev.permanentAddress,
        [name]: value,
      },
    }));
  };

  useEffect(() => {
    const matchedRoute = transport.find(
      (route) =>
        route.stoppage === formData.transportFacility.busStop &&
        route.vehicleNumber === formData.transportFacility.vehicleNo,
    );

    if (matchedRoute) {
      setFormData((prev) => ({
        ...prev,
        transportFacility: {
          ...prev.transportFacility,
          busNumber: matchedRoute.busNo,
        },
      }));
    }
  }, [
    formData.transportFacility.busStop,
    formData.transportFacility.vehicleNo,
    transport,
  ]);

  const handleTransportFacilityChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      transportFacility: {
        ...prev.transportFacility,
        [name]: value,
      },
    }));
  };

  const handleCheckboxChange = (e) => {
    const checked = e.target.checked;
    setIsSameAddress(checked);

    setFormData((prev) => ({
      ...prev,
      permanentAddress: checked
        ? { ...prev.presentAddress }
        : { address: "", city: "", pincode: "", state: "" },
    }));
  };

  const handleCertificateChange = (item) => {
    setFormData((prevData) => {
      const isSelected = prevData.certificate.includes(item);

      return {
        ...prevData,
        certificate: isSelected
          ? prevData.certificate.filter((cert) => cert !== item)
          : [...prevData.certificate, item],
      };
    });
  };

  // ── Subjects (8 independent selects, none required) ─────────────────────
  const handleSubjectSelectChange = (index, selected) => {
    setSubjectSelections((prev) => {
      const updated = [...prev];
      updated[index] = selected?.value || "";
      return updated;
    });
  };

  useEffect(() => {
    const filteredSubjects = subjectSelections.filter(
      (subject) => subject && subject !== "",
    );
    setFormData((prev) => ({
      ...prev,
      subjects: filteredSubjects,
    }));
  }, [subjectSelections]);

  const handleScholarshipChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ["admissionFee", "monthlyFee", "developmentFee"];
    const updatedValue = numericFields.includes(name)
      ? value.replace(/\D/g, "") || "0"
      : value;

    setFormData((prev) => {
      let updatedScholarship = { ...prev.scholarship };

      if (name === "type") {
        updatedScholarship = {
          type: updatedValue,
          siblingAdmissionNo: [],
          staffId: "",
          scholarshipFrom: "",
          discount: {
            admissionFee: 0,
            monthlyFee: 0,
            developmentFee: 0,
          },
        };
        setSiblingAdmissionNos([]);
        setStaffId("");
      } else if (numericFields.includes(name)) {
        updatedScholarship.discount = {
          ...updatedScholarship.discount,
          [name]: updatedValue,
        };
      } else {
        updatedScholarship[name] = updatedValue;
      }

      return { ...prev, scholarship: updatedScholarship };
    });
  };

  const addSiblingAdmissionNo = () => {
    if (
      newSiblingAdmissionNo &&
      formData.scholarship?.siblingAdmissionNo?.length < 5
    ) {
      setFormData((prev) => ({
        ...prev,
        scholarship: {
          ...prev.scholarship,
          siblingAdmissionNo: [
            ...(prev.scholarship.siblingAdmissionNo || []),
            newSiblingAdmissionNo,
          ],
        },
      }));

      setNewSiblingAdmissionNo("");
    }
  };

  const removeSiblingAdmissionNo = (index) => {
    setFormData((prev) => ({
      ...prev,
      scholarship: {
        ...prev.scholarship,
        siblingAdmissionNo: prev.scholarship.siblingAdmissionNo.filter(
          (_, i) => i !== index,
        ),
      },
    }));
  };

  const addEmployeeId = () => {
    if (!staffId && formData.scholarship?.staffId) {
      setStaffId(formData.scholarship.staffId);
    }
  };

  const countAddressFilledFields = (obj1, obj2) => {
    return String(
      [...Object.values(obj1), ...Object.values(obj2)].filter(
        (value) => value !== "",
      ).length,
    ).padStart(2, "0");
  };

  const getAddressTotalFields = (obj1, obj2) => {
    return String(Object.keys(obj1).length + Object.keys(obj2).length).padStart(
      2,
      "0",
    );
  };

  const countFilledFields = (obj) => {
    return String(
      Object.values(obj).filter((value) => value !== "").length,
    ).padStart(2, "0");
  };

  const getTotalFields = (obj) => {
    return String(Object.keys(obj).length).padStart(2, "0");
  };

  const countParentFilledFields = (arr) => {
    return String(
      arr.reduce((count, obj) => {
        return (
          count + Object.values(obj).filter((value) => value !== "").length
        );
      }, 0),
    ).padStart(2, "0");
  };

  const getParentTotalFields = (arr) => {
    return String(
      arr.reduce((count, obj) => count + Object.keys(obj).length, 0),
    ).padStart(2, "0");
  };

  const countFilled = countParentFilledFields(formData.parentDetails);
  const totalFields = getParentTotalFields(formData.parentDetails);
  const isCompleted = countFilled === totalFields;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (uploading) {
      toast.warn("Please Wait, Uploading Image", {
        position: "top-right",
      });
      return;
    }

    setLoading(true);

    const sanitizedParentDetails = formData.parentDetails.map((parent) => {
      if (parent.relation === "Other" && parent.customRelation) {
        const { customRelation, ...rest } = parent;
        return { ...rest, relation: customRelation };
      }
      const { customRelation, ...rest } = parent;
      return rest;
    });

    const payload = {
      ...formData,
      parentDetails: sanitizedParentDetails,
    };

    try {
      const response = await axios.post(
        `${server}/addStudentsUsingMongodb`,
        formData,
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      const { statusCode, body } = response.data?.["body-json"] || {};
      if (statusCode && statusCode !== 200) {
        let errorMessage = "Something went wrong";

        try {
          const parsedBody = JSON.parse(body);
          errorMessage = parsedBody.message || errorMessage;
        } catch (parseError) {
          console.error("Error parsing response body:", parseError);
        }

        throw new Error(errorMessage);
      }
      toast.success("New Student Added", { position: "top-right" });
      navigate("/student-details");
      setFormData(initialState);
    } catch (error) {
      console.error("Error:", error);
      const errorMessage =
        error.response?.data?.message || error.message || "Please Try Again";

      toast.error(errorMessage, { position: "top-right" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${server}/getAllBusRoutesMongoDb`, {
        headers: {
          Authorization: `${localStorage.getItem("token")}`,
        },
      })
      .then((response) => {
        const routes = response.data["body-json"]?.body?.routes;
        if (Array.isArray(routes)) {
          setTransport(routes);
        } else {
          console.error("Invalid data format from API", response.data);
        }
        setLoading(false);
      })
      .catch((error) => {
        console.error("API Error:", error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (!formData.currentClass) return;
    setLoading(true);
    axios
      .post(
        `${server}/classHouseCount`,
        { currentClass: formData.currentClass },
        {
          headers: {
            Authorization: `${localStorage.getItem("token")}`,
          },
        },
      )
      .then((response) => {
        const houseCounts = response.data?.["body-json"]?.body?.houseCounts;

        if (houseCounts && typeof houseCounts === "object") {
          setHouseCount(houseCounts);

          const minHouse = Object.entries(houseCounts).reduce((min, curr) =>
            curr[1] < min[1] ? curr : min,
          )[0];
          setSelectedHouse(minHouse);
          setFormData((prev) => ({
            ...prev,
            studentDetails: {
              ...prev.studentDetails,
              house: minHouse,
            },
          }));
        } else {
          console.error("Invalid data format from API", response.data);
        }
      })
      .catch((error) => {
        console.error("API Error:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [formData.currentClass]);

  const handleUploadStudentImage = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setPreview(URL.createObjectURL(file));
    setUploading(true);

    try {
      const { data } = await axios.post(
        "https://7otfgyv0ke.execute-api.ap-south-1.amazonaws.com/prod/studentImagePresigned",
        { fileName: file.name },
      );
      const uploadDetails = data?.["body-json"]?.body?.uploadDetails?.[0];
      if (!uploadDetails?.signedUrl || !uploadDetails?.key) {
        throw new Error("Invalid upload details from server");
      }
      await axios.put(uploadDetails.signedUrl, file, {
        headers: { "Content-Type": file.type },
      });
      setImage(uploadDetails.key);
      setFormData((prev) => ({
        ...prev,
        studentDetails: {
          ...prev.studentDetails,
          studentImage: uploadDetails.key,
        },
      }));
    } catch (error) {
      console.error("Upload failed:", error);
    } finally {
      setUploading(false);
    }
  };

  const showStreamField = isSeniorSecondaryClass(formData.currentClass);

  useEffect(() => {
    if (
      !showStreamField &&
      (formData.stream || formData.studentDetails?.stream)
    ) {
      setFormData((prev) => ({
        ...prev,
        stream: "",
        studentDetails: { ...prev.studentDetails, stream: "" },
      }));
    }
  }, [showStreamField]);

  useEffect(() => {
    if (!formData.stream || !SUBJECT_COMBINATIONS[formData.stream]) {
      if (formData.studentDetails.subjects?.length) {
        setFormData((prev) => ({
          ...prev,
          studentDetails: { ...prev.studentDetails, subjects: [] },
        }));
      }
      return;
    }

    const { compulsory, optionalGroups } =
      SUBJECT_COMBINATIONS[formData.stream];
    const validOptional = optionalGroups.flatMap((g) => g.options);

    setFormData((prev) => {
      // keep any previously chosen optional subjects still valid for this stream
      const keptOptional = (prev.studentDetails.subjects || []).filter(
        (subj) => !compulsory.includes(subj) && validOptional.includes(subj),
      );
      return {
        ...prev,
        studentDetails: {
          ...prev.studentDetails,
          subjects: [...compulsory, ...keptOptional],
        },
      };
    });
  }, [formData.stream]);

  const handleOptionalSubjectsChange = (selectedOptions) => {
    const compulsory = SUBJECT_COMBINATIONS[formData.stream]?.compulsory || [];
    const optional = (selectedOptions || []).map((opt) => opt.value);
    setFormData((prev) => ({
      ...prev,
      studentDetails: {
        ...prev.studentDetails,
        subjects: [...compulsory, ...optional],
      },
    }));
  };

  const studentDetailsForCount = showStreamField
    ? formData.studentDetails
    : (() => {
        const { stream, ...rest } = formData.studentDetails;
        return rest;
      })();

  return (
    <>
      <div id="db-wrapper">
        <main id="page-content">
          <div>
            <div class="container-fluid">
              <div className="row g-2">
                <div className="col-lg-8">
                  <div class="m-3 mt-2 d-flex justify-content-between">
                    <div class="d-flex align-items-center mt-2">
                      <span
                        onClick={() => window.history.back()}
                        class="back-button me-3 cursor-pointer"
                      >
                        <i className="bi bi-arrow-left fs-3"></i>
                      </span>
                      <nav aria-label="breadcrumb">
                        <h4 className="m-0 lh-sm">Add Student</h4>
                        <ol class="breadcrumb pe-3 lh-sm">
                          <li class="breadcrumb-item">
                            <a href="#">Student Information</a>
                          </li>
                          <li class="breadcrumb-item">
                            <a href="#">Student Details</a>
                          </li>
                          <li
                            class="breadcrumb-item active"
                            aria-current="page"
                          >
                            Add Student
                          </li>
                        </ol>
                      </nav>
                    </div>
                  </div>

                  <form onSubmit={handleSubmit}>
                    <div class="px-5 py-3 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingOne">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseOne"
                              aria-controls="collapseOne"
                            >
                              <span class="me-auto fs-5">
                                1. Student Details{" "}
                                <span className="ms-3 text-secondary">
                                  ({countFilledFields(studentDetailsForCount)}/
                                  {getTotalFields(studentDetailsForCount)})
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span
                                  className={`badge me-5 px-3 ${
                                    countFilledFields(
                                      studentDetailsForCount,
                                    ) === getTotalFields(studentDetailsForCount)
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  {countFilledFields(studentDetailsForCount) ===
                                  getTotalFields(studentDetailsForCount)
                                    ? "Completed"
                                    : "Required"}
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseOne"
                            class="collapse"
                            aria-labelledby="headingOne"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 mt-2 mb-3">
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Session <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={sessionOptions}
                                  value={
                                    sessionOptions.find(
                                      (opt) => opt.value === formData.session,
                                    ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      session: selected?.value || "",
                                    }))
                                  }
                                  placeholder="Select Session"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.session
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Student Id
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.studentId
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="studentId"
                                  placeholder="..."
                                  value={formData.studentDetails?.studentId}
                                  onChange={handleStudentDetailsChange}
                                  required
                                  readOnly
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Admission Date{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.admissionDate
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="admissionDate"
                                  placeholder="..."
                                  value={formData.studentDetails?.admissionDate}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Full Name{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.name
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="name"
                                  placeholder="Enter Full Name"
                                  value={formData.studentDetails?.name}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Admission in Class{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={classList.map((cls) => ({
                                    label: cls,
                                    value: cls,
                                  }))}
                                  value={classList
                                    .map((cls) => ({ label: cls, value: cls }))
                                    .find(
                                      (opt) =>
                                        opt.value ===
                                        formData.studentDetails
                                          ?.admissionInClass,
                                    )}
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        admissionInClass: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Class"
                                  classNamePrefix="react-select"
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Section <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={sectionList.map((section) => ({
                                    label: section,
                                    value: section,
                                  }))}
                                  value={
                                    sectionList
                                      .map((section) => ({
                                        label: section,
                                        value: section,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails
                                            ?.admissionInSection,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        admissionInSection:
                                          selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Section"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.admissionInSection
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Current Class{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={classList.map((cls) => ({
                                    label: cls,
                                    value: cls,
                                  }))}
                                  value={
                                    classList
                                      .map((cls) => ({
                                        label: cls,
                                        value: cls,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value === formData.currentClass,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      currentClass: selected?.value || "",
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        currentClass: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Current Class"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.currentClass ? "border-danger" : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Section <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={sectionList.map((section) => ({
                                    label: section,
                                    value: section,
                                  }))}
                                  value={
                                    sectionList
                                      .map((section) => ({
                                        label: section,
                                        value: section,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails
                                            ?.currentSection,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        currentSection: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Current Section"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.currentSection
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              {showStreamField && (
                                <div className="col-lg-6 col-12">
                                  <label class="form-label mb-1 ms-3">
                                    Stream{" "}
                                    <span className="text-danger">*</span>
                                  </label>
                                  <Select
                                    options={STREAM_OPTIONS}
                                    value={
                                      STREAM_OPTIONS.find(
                                        (opt) => opt.value === formData.stream,
                                      ) || null
                                    }
                                    onChange={(selected) =>
                                      setFormData((prev) => ({
                                        ...prev,
                                        stream: selected?.value || "",
                                        studentDetails: {
                                          ...prev.studentDetails,
                                          stream: selected?.value || "",
                                        },
                                      }))
                                    }
                                    placeholder="Select Stream"
                                    classNamePrefix="react-select"
                                    className={`text-dark ${
                                      errors?.stream ? "border-danger" : ""
                                    }`}
                                    isClearable
                                  />
                                </div>
                              )}
                              {showStreamField && formData.stream && (
                                <>
                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Compulsory Subjects
                                    </label>
                                    <Select
                                      isMulti
                                      isDisabled
                                      options={[]}
                                      value={SUBJECT_COMBINATIONS[
                                        formData.stream
                                      ].compulsory.map((s) => ({
                                        label: s,
                                        value: s,
                                      }))}
                                      classNamePrefix="react-select"
                                      placeholder="Auto-selected"
                                    />
                                  </div>

                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Optional Subjects{" "}
                                      <span className="text-danger">*</span>
                                    </label>
                                    <Select
                                      isMulti
                                      options={getOptionalSubjectGroups(
                                        formData.stream,
                                      )}
                                      value={(
                                        formData.studentDetails.subjects || []
                                      )
                                        .filter(
                                          (subj) =>
                                            !SUBJECT_COMBINATIONS[
                                              formData.stream
                                            ].compulsory.includes(subj),
                                        )
                                        .map((s) => ({ label: s, value: s }))}
                                      onChange={handleOptionalSubjectsChange}
                                      placeholder="Select Optional Subject(s)"
                                      classNamePrefix="react-select"
                                      className={`text-dark ${
                                        errors?.studentDetails?.subjects
                                          ? "border-danger"
                                          : ""
                                      }`}
                                    />
                                  </div>
                                </>
                              )}
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Roll No.
                                </label>
                                <input
                                  type="number"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.rollNo
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="rollNo"
                                  placeholder="Enter Roll No."
                                  value={formData.studentDetails?.rollNo}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Select Gender{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={[
                                    { label: "Male", value: "Male" },
                                    { label: "Female", value: "Female" },
                                  ]}
                                  value={
                                    ["Male", "Female"]
                                      .map((g) => ({ label: g, value: g }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails?.gender,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        gender: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Gender"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.gender
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Date of Birth{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="date"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.dateOfBirth
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="dateOfBirth"
                                  placeholder="..."
                                  value={formData.studentDetails?.dateOfBirth}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Category{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={categoryList.map((category) => ({
                                    label: category,
                                    value: category,
                                  }))}
                                  value={
                                    categoryList
                                      .map((category) => ({
                                        label: category,
                                        value: category,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails?.category,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        category: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Category"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.category
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  House <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.house
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="house"
                                  value={selectedHouse}
                                  placeholder="..."
                                  onChange={(e) =>
                                    setSelectedHouse(e.target.value)
                                  }
                                  required
                                  readOnly
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Blood Group{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={bloodGroupList.map((bg) => ({
                                    label: bg,
                                    value: bg,
                                  }))}
                                  value={
                                    bloodGroupList
                                      .map((bg) => ({ label: bg, value: bg }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails?.bloodGroup,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        bloodGroup: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Blood Group"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.bloodGroup
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Aadhaar No{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  maxLength={12}
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.aadhar_no
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="aadhar_no"
                                  placeholder="Enter Aadhaar No."
                                  value={formData.studentDetails?.aadhar_no}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Apaar ID{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  maxLength={12}
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.apaarId
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="apaarId"
                                  placeholder="Enter Apaar ID"
                                  value={formData.studentDetails?.apaarId}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  PEN <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  maxLength={12}
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.pen
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="pen"
                                  placeholder="Enter PEN"
                                  value={formData.studentDetails?.pen}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Bank Account No{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.accountNo
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="accountNo"
                                  placeholder="Enter Bank Account No."
                                  value={formData.studentDetails?.accountNo}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Religion{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={religionList.map((religion) => ({
                                    label: religion,
                                    value: religion,
                                  }))}
                                  value={
                                    religionList
                                      .map((religion) => ({
                                        label: religion,
                                        value: religion,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails?.religion,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        religion: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Religion"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.religion
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Handicap{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={[
                                    { label: "No", value: "No" },
                                    { label: "Yes", value: "Yes" },
                                  ]}
                                  value={
                                    ["No", "Yes"]
                                      .map((handicap) => ({
                                        label: handicap,
                                        value: handicap,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails?.handicap,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        handicap: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Handicap"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.handicap
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Previous School Name{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.previousSchoolName
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="previousSchoolName"
                                  placeholder="Enter Previous School Name"
                                  value={
                                    formData.studentDetails?.previousSchoolName
                                  }
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Previous School Address{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control text-dark ${
                                    errors?.studentDetails
                                      ?.previousSchoolAddress
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="previousSchoolAddress"
                                  placeholder="Enter Previous School Address"
                                  value={
                                    formData.studentDetails
                                      ?.previousSchoolAddress
                                  }
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>

                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Under Mid Session{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={[
                                    { label: "No", value: "No" },
                                    { label: "Yes", value: "Yes" },
                                  ]}
                                  value={
                                    ["No", "Yes"]
                                      .map((option) => ({
                                        label: option,
                                        value: option,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails
                                            ?.underMidSection,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        underMidSection: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.underMidSection
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Applicable Month{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <Select
                                  options={monthsList.map((month) => ({
                                    label: month,
                                    value: month,
                                  }))}
                                  value={
                                    monthsList
                                      .map((month) => ({
                                        label: month,
                                        value: month,
                                      }))
                                      .find(
                                        (opt) =>
                                          opt.value ===
                                          formData.studentDetails
                                            ?.applicableMonth,
                                      ) || null
                                  }
                                  onChange={(selected) =>
                                    setFormData((prev) => ({
                                      ...prev,
                                      studentDetails: {
                                        ...prev.studentDetails,
                                        applicableMonth: selected?.value || "",
                                      },
                                    }))
                                  }
                                  placeholder="Select Month"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.studentDetails?.applicableMonth
                                      ? "border-danger"
                                      : ""
                                  }`}
                                />
                              </div>

                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Primary Phone No.{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  maxLength={10}
                                  className={`form-control text-dark ${
                                    errors?.studentDetails?.phoneNo
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  name="phoneNo"
                                  placeholder="Enter Primary Phone No."
                                  value={formData.studentDetails?.phoneNo}
                                  onChange={handleStudentDetailsChange}
                                  required
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingTwo">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseTwo"
                            >
                              <span class="me-auto fs-5">
                                2. Parent Details{" "}
                                <span className="ms-3 text-secondary">
                                  ({countFilled}/{totalFields})
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span
                                  className={`badge me-5 px-3 ${
                                    isCompleted
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  {isCompleted ? "Completed" : "Required"}
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseTwo"
                            class="collapse"
                            aria-labelledby="headingTwo"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 mb-3">
                              {formData.parentDetails.map((parent, index) => (
                                <div key={index} className="col-lg-6 col-12">
                                  <p className="px-4 py-3 bg-light rounded-3 text-dark mt-3 mb-1 lh-sm">
                                    {parent.relation}'s Details
                                    <br />
                                    <span className="text-secondary">
                                      (All Details are Compulsory)
                                    </span>
                                  </p>
                                  <div className="row g-3 mb-2">
                                    <div className="col-12">
                                      {index <= 1 ? (
                                        ""
                                      ) : (
                                        <>
                                          <label className="form-label mb-1 ms-3 mt-3">
                                            Relation with Student{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </label>
                                          <Select
                                            options={relationshipsList.map(
                                              (relation) => ({
                                                label: relation,
                                                value: relation,
                                              }),
                                            )}
                                            value={
                                              relationshipsList
                                                .map((relation) => ({
                                                  label: relation,
                                                  value: relation,
                                                }))
                                                .find(
                                                  (opt) =>
                                                    opt.value ===
                                                    parent.relation,
                                                ) || null
                                            }
                                            onChange={(selected) =>
                                              handleParentDetailsChange(index, {
                                                target: {
                                                  name: "relation",
                                                  value: selected?.value || "",
                                                },
                                              })
                                            }
                                            placeholder="Select Relation"
                                            classNamePrefix="react-select"
                                            isClearable
                                          />
                                        </>
                                      )}
                                    </div>

                                    {index > 1 &&
                                      parent.relation === "Other" && (
                                        <div className="col-12">
                                          <label className="form-label mb-1 ms-3">
                                            Specify Relation{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </label>
                                          <input
                                            type="text"
                                            className={`form-control text-dark ${
                                              errors?.parentDetails?.[index]
                                                ?.customRelation
                                                ? "border-danger"
                                                : ""
                                            }`}
                                            placeholder="Enter Relation (e.g. Uncle, Aunt)"
                                            name="customRelation"
                                            value={parent.customRelation || ""}
                                            onChange={(e) =>
                                              handleParentDetailsChange(index, {
                                                target: {
                                                  name: "customRelation",
                                                  value: e.target.value,
                                                },
                                              })
                                            }
                                            required
                                          />
                                        </div>
                                      )}

                                    {Object.keys(parent).map((key) => {
                                      if (
                                        key === "relation" ||
                                        key === "customRelation"
                                      )
                                        return null;
                                      return (
                                        <div key={key} className="col-12">
                                          <label className="form-label mb-1 ms-3">
                                            {key.charAt(0).toUpperCase() +
                                              key.slice(1)}{" "}
                                            <span className="text-danger">
                                              *
                                            </span>
                                          </label>
                                          <input
                                            type="text"
                                            className={`form-control text-dark ${
                                              errors?.parentDetails?.[index]?.[
                                                key
                                              ]
                                                ? "border-danger"
                                                : ""
                                            }`}
                                            placeholder={`Enter ${
                                              key.charAt(0).toUpperCase() +
                                              key.slice(1)
                                            }`}
                                            name={key}
                                            value={parent[key] || ""}
                                            onChange={(e) => {
                                              let value = e.target.value;
                                              if (key === "phone") {
                                                value = value
                                                  .replace(/\D/g, "")
                                                  .slice(0, 10);
                                              }

                                              if (key === "aadhaarNo") {
                                                value = value
                                                  .replace(/\D/g, "")
                                                  .slice(0, 12);
                                              }

                                              handleParentDetailsChange(index, {
                                                target: { name: key, value },
                                              });
                                            }}
                                            required
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingThree">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseThree"
                              aria-controls="collapseThree"
                            >
                              <span class="me-auto fs-5">
                                3. Address{" "}
                                <span className="ms-3 text-secondary">
                                  (
                                  {countAddressFilledFields(
                                    formData.presentAddress,
                                    formData.permanentAddress,
                                  )}
                                  /
                                  {getAddressTotalFields(
                                    formData.presentAddress,
                                    formData.permanentAddress,
                                  )}
                                  )
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span
                                  className={`badge me-5 px-3 ${
                                    countAddressFilledFields(
                                      formData.presentAddress,
                                      formData.permanentAddress,
                                    ) ===
                                    getAddressTotalFields(
                                      formData.presentAddress,
                                      formData.permanentAddress,
                                    )
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  {countAddressFilledFields(
                                    formData.presentAddress,
                                    formData.permanentAddress,
                                  ) ===
                                  getAddressTotalFields(
                                    formData.presentAddress,
                                    formData.permanentAddress,
                                  )
                                    ? "Completed"
                                    : "Required"}
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseThree"
                            class="collapse"
                            aria-labelledby="headingThree"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 my-3">
                              <div className="col-lg-6 col-12">
                                <p className="px-4 py-3 bg-light rounded-3 text-dark mb-3 lh-sm">
                                  Present Address
                                  <br />
                                  <span className="text-secondary">
                                    (All Details are Compulsory)
                                  </span>
                                </p>
                                <div className="">
                                  <div className="row g-2 mb-2">
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        Address
                                        <span className="text-danger">*</span>
                                      </label>
                                      <textarea
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.presentAddress?.address
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="address"
                                        value={formData.presentAddress.address}
                                        onChange={handlePresentAddressChange}
                                        placeholder="Enter Address"
                                        rows="3"
                                        required
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        State
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Select
                                        options={statesList.map((state) => ({
                                          label: state,
                                          value: state,
                                        }))}
                                        value={
                                          statesList
                                            .map((state) => ({
                                              label: state,
                                              value: state,
                                            }))
                                            .find(
                                              (opt) =>
                                                opt.value ===
                                                formData.presentAddress.state,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                          handlePresentAddressChange({
                                            target: {
                                              name: "state",
                                              value: selected?.value || "",
                                            },
                                          })
                                        }
                                        placeholder="Select State"
                                        classNamePrefix="react-select"
                                        className={`text-dark ${
                                          errors?.presentAddress?.state
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        required
                                        isClearable
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        City
                                        <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.presentAddress?.city
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="city"
                                        value={formData.presentAddress.city}
                                        onChange={handlePresentAddressChange}
                                        placeholder="Enter City"
                                        required
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        Pincode
                                        <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.presentAddress?.pincode
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="pincode"
                                        value={formData.presentAddress.pincode}
                                        onChange={handlePresentAddressChange}
                                        placeholder="Enter Pincode"
                                        required
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div className="col-lg-6 col-12">
                                <div className="px-4 py-3 bg-light rounded-3 mb-3 d-flex align-items-center justify-content-between">
                                  <p className="text-dark lh-sm m-0">
                                    Permanent Address
                                    <br />
                                    <span className="text-secondary">
                                      (Same as Present Address)
                                    </span>
                                  </p>
                                  <div className="d-flex">
                                    <input
                                      type="checkbox"
                                      className="form-check-input ms-3"
                                      checked={isSameAddress}
                                      onChange={handleCheckboxChange}
                                    />
                                  </div>
                                </div>
                                <div className="">
                                  <div className="row g-2">
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        Address
                                        <span className="text-danger">*</span>
                                      </label>
                                      <textarea
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.permanentAddress?.address
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="address"
                                        value={
                                          formData.permanentAddress.address
                                        }
                                        onChange={handlePermanentAddressChange}
                                        placeholder="Enter Address"
                                        rows="3"
                                        required
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        State
                                        <span className="text-danger">*</span>
                                      </label>
                                      <Select
                                        options={statesList.map((state) => ({
                                          label: state,
                                          value: state,
                                        }))}
                                        value={
                                          statesList
                                            .map((state) => ({
                                              label: state,
                                              value: state,
                                            }))
                                            .find(
                                              (opt) =>
                                                opt.value ===
                                                formData.permanentAddress.state,
                                            ) || null
                                        }
                                        onChange={(selected) =>
                                          handlePermanentAddressChange({
                                            target: {
                                              name: "state",
                                              value: selected?.value || "",
                                            },
                                          })
                                        }
                                        placeholder="Select State"
                                        classNamePrefix="react-select"
                                        className={`text-dark ${
                                          errors?.permanentAddress?.state
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        isClearable
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        City
                                        <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.permanentAddress?.city
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="city"
                                        value={formData.permanentAddress.city}
                                        onChange={handlePermanentAddressChange}
                                        placeholder="Enter City"
                                        required
                                      />
                                    </div>
                                    <div className="col-12">
                                      <label class="form-label mb-1 ms-3">
                                        Pincode
                                        <span className="text-danger">*</span>
                                      </label>
                                      <input
                                        type="text"
                                        className={`form-control text-dark ${
                                          errors?.permanentAddress?.pincode
                                            ? "border-danger"
                                            : ""
                                        }`}
                                        name="pincode"
                                        value={
                                          formData.permanentAddress.pincode
                                        }
                                        onChange={handlePermanentAddressChange}
                                        placeholder="Enter Pincode"
                                        required
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingFour">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseFour"
                              aria-controls="collapseFour"
                            >
                              <span class="me-auto fs-5">
                                4. Transport Facility{" "}
                                <span className="ms-3 text-secondary">
                                  (
                                  {countFilledFields(
                                    formData.transportFacility,
                                  )}
                                  /{getTotalFields(formData.transportFacility)})
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span className="badge me-5 px-3 bg-info-soft">
                                  If Availing
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseFour"
                            class="collapse"
                            aria-labelledby="headingFour"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 mt-2 mb-3">
                              <div className="col-lg-6 col-12">
                                <label className="form-label mb-1 ms-3">
                                  Bus Stoppage
                                </label>
                                <Select
                                  options={Array.from(
                                    new Set(
                                      transport.map((route) => route.stoppage),
                                    ),
                                  )
                                    .sort((a, b) => a.localeCompare(b))
                                    .map((stoppage) => ({
                                      label: stoppage,
                                      value: stoppage,
                                    }))}
                                  value={
                                    formData.transportFacility.busStop
                                      ? {
                                          label:
                                            formData.transportFacility.busStop,
                                          value:
                                            formData.transportFacility.busStop,
                                        }
                                      : null
                                  }
                                  onChange={(selected) =>
                                    handleTransportFacilityChange({
                                      target: {
                                        name: "busStop",
                                        value: selected?.value || "",
                                      },
                                    })
                                  }
                                  placeholder="Select Bus Stop"
                                  classNamePrefix="react-select"
                                  className="text-dark"
                                  isClearable
                                />
                              </div>

                              <div className="col-lg-6 col-12">
                                <label className="form-label mb-1 ms-3">
                                  Vehicle No.
                                </label>
                                <Select
                                  options={transport
                                    .filter(
                                      (route) =>
                                        route.stoppage ===
                                        formData.transportFacility.busStop,
                                    )
                                    .map((route) => ({
                                      label: route.vehicleNumber,
                                      value: route.vehicleNumber,
                                    }))}
                                  value={
                                    formData.transportFacility.vehicleNo
                                      ? {
                                          label:
                                            formData.transportFacility
                                              .vehicleNo,
                                          value:
                                            formData.transportFacility
                                              .vehicleNo,
                                        }
                                      : null
                                  }
                                  onChange={(selected) =>
                                    handleTransportFacilityChange({
                                      target: {
                                        name: "vehicleNo",
                                        value: selected?.value || "",
                                      },
                                    })
                                  }
                                  placeholder="Select Vehicle Number"
                                  classNamePrefix="react-select"
                                  className="text-dark"
                                  isDisabled={
                                    !formData.transportFacility.busStop
                                  }
                                  isClearable
                                />
                              </div>

                              <div className="col-lg-6 col-12">
                                <label className="form-label mb-1 ms-3">
                                  Bus No.
                                </label>
                                <input
                                  type="number"
                                  className="form-control text-dark"
                                  name="busNumber"
                                  value={
                                    formData.transportFacility.busNumber || ""
                                  }
                                  readOnly
                                />
                              </div>

                              <div className="col-lg-6 col-12">
                                <label className="form-label mb-1 ms-3">
                                  Applicable Month
                                </label>
                                <Select
                                  options={monthsList.map((monthName) => ({
                                    label: monthName,
                                    value: monthName,
                                  }))}
                                  value={
                                    monthsList.includes(
                                      formData.transportFacility
                                        ?.applicableMonth,
                                    )
                                      ? {
                                          label:
                                            formData.transportFacility
                                              ?.applicableMonth,
                                          value:
                                            formData.transportFacility
                                              ?.applicableMonth,
                                        }
                                      : null
                                  }
                                  onChange={(selected) =>
                                    handleTransportFacilityChange({
                                      target: {
                                        name: "applicableMonth",
                                        value: selected?.value || "",
                                      },
                                    })
                                  }
                                  placeholder="Select Applicable Month"
                                  classNamePrefix="react-select"
                                  className="text-dark"
                                  isClearable
                                />
                              </div>
                            </div>

                            {formData.transportFacility.vehicleNo && (
                              <BusSeatView
                                vehicleNumber={
                                  formData.transportFacility.vehicleNo
                                }
                              />
                            )}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingFive">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseFive"
                              aria-controls="collapseFive"
                            >
                              <span class="me-auto fs-5">
                                5. Certificate{" "}
                                <span className="ms-3 text-secondary">
                                  ({countFilledFields(formData.certificate)}/
                                  {certificatesList.length})
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span
                                  className={`badge me-5 px-3 ${
                                    countFilledFields(formData.certificate) >= 5
                                      ? "bg-success-soft"
                                      : "bg-danger-soft"
                                  }`}
                                >
                                  {countFilledFields(formData.certificate) >= 5
                                    ? "Completed"
                                    : "Required"}
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseFive"
                            class="collapse"
                            aria-labelledby="headingFive"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-3 py-2">
                              <div className="col-12 pt-1">
                                {certificatesList.map((item, index) => (
                                  <li
                                    className={`list-group-item d-flex align-items-center justify-content-between px-0 py-2 ${
                                      index === certificatesList.length - 1
                                        ? ""
                                        : "border-bottom"
                                    }`}
                                    key={index}
                                  >
                                    <div>
                                      {index + 1}. {item}
                                    </div>
                                    <div>
                                      <div className="form-check form-switch">
                                        <input
                                          type="checkbox"
                                          className="form-check-input mx-0"
                                          checked={formData.certificate.includes(
                                            item,
                                          )}
                                          onChange={() =>
                                            handleCertificateChange(item)
                                          }
                                        />
                                      </div>
                                    </div>
                                  </li>
                                ))}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingSix">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseSix"
                              aria-controls="collapseSix"
                            >
                              <span class="me-auto fs-5">6. Scholarship</span>
                              <div className="d-flex align-items-center">
                                <span className="badge me-5 px-3 bg-info-soft">
                                  If Eligible
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseSix"
                            class="collapse"
                            aria-labelledby="headingSix"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 mt-2 mb-3">
                              <div className="col-lg-6 col-12">
                                <label class="form-label mb-1 ms-3">
                                  Ward Type
                                </label>
                                <Select
                                  options={[
                                    { label: "Sibling", value: "sibling" },
                                    {
                                      label: "Staff Child",
                                      value: "staff",
                                    },
                                    { label: "Other", value: "other" },
                                  ]}
                                  value={
                                    formData.scholarship?.type
                                      ? {
                                          label:
                                            formData.scholarship.type ===
                                            "sibling"
                                              ? "Sibling"
                                              : formData.scholarship.type ===
                                                  "staff"
                                                ? "Staff Child"
                                                : "Other",
                                          value: formData.scholarship.type,
                                        }
                                      : null
                                  }
                                  onChange={(selected) =>
                                    handleScholarshipChange({
                                      target: {
                                        name: "type",
                                        value: selected?.value || "",
                                      },
                                    })
                                  }
                                  placeholder="Select Scholarship Type"
                                  classNamePrefix="react-select"
                                  className={`text-dark ${
                                    errors?.scholarship?.type
                                      ? "border-danger"
                                      : ""
                                  }`}
                                  isClearable
                                />
                              </div>

                              {formData.scholarship?.type === "sibling" && (
                                <>
                                  <div className="col-lg-6 col-12"></div>
                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Sibling Admission No.
                                    </label>

                                    <input
                                      type="number"
                                      className={`form-control text-dark ${
                                        errors?.scholarship?.siblingAdmissionNo
                                          ? "border-danger"
                                          : ""
                                      }`}
                                      name="siblingAdmissionNo"
                                      value={newSiblingAdmissionNo}
                                      onChange={(e) =>
                                        setNewSiblingAdmissionNo(e.target.value)
                                      }
                                    />

                                    <span
                                      className="btn btn-danger px-5 mt-3 mb-3 fs-6 fw-semibold rounded-pill cursor-pointer"
                                      onClick={addSiblingAdmissionNo}
                                      disabled={
                                        formData.scholarship?.siblingAdmissionNo
                                          ?.length >= 5
                                      }
                                    >
                                      Add Sibling
                                      <i className="bi bi-plus-circle-fill ms-2"></i>
                                    </span>
                                  </div>
                                </>
                              )}

                              {formData.scholarship?.type === "staff" && (
                                <>
                                  <div className="col-lg-6 col-12"></div>
                                  <div className="col-lg-6 col-12">
                                    <label class="form-label mb-1 ms-3">
                                      Staff ID
                                    </label>
                                    <input
                                      type="text"
                                      className={`form-control text-dark ${
                                        errors?.scholarship?.staffId
                                          ? "border-danger"
                                          : ""
                                      }`}
                                      name="staffId"
                                      value={formData.scholarship?.staffId}
                                      onChange={handleScholarshipChange}
                                    />
                                    <span
                                      class="btn btn-danger px-5 mt-3 mb-3 fs-6 fw-semibold rounded-pill cursor-pointer"
                                      onClick={addEmployeeId}
                                      disabled={!!staffId}
                                    >
                                      Add Staff
                                      <i className="bi bi-plus-circle-fill ms-2"></i>
                                    </span>
                                  </div>
                                </>
                              )}

                              {formData.scholarship?.type === "other" && (
                                <>
                                  <div className="col-lg-6 col-12"></div>
                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Admission Fee
                                    </label>
                                    <input
                                      type="number"
                                      className={`form-control text-dark ${
                                        errors?.scholarship?.admissionFee
                                          ? "border-danger"
                                          : ""
                                      }`}
                                      name="admissionFee"
                                      placeholder="..."
                                      value={
                                        formData.scholarship?.discount
                                          ?.admissionFee || ""
                                      }
                                      onChange={handleScholarshipChange}
                                    />
                                  </div>
                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Monthly Fee
                                    </label>
                                    <input
                                      type="number"
                                      className={`form-control text-dark ${
                                        errors?.scholarship?.monthlyFee
                                          ? "border-danger"
                                          : ""
                                      }`}
                                      name="monthlyFee"
                                      placeholder="..."
                                      value={
                                        formData.scholarship?.discount
                                          ?.monthlyFee || ""
                                      }
                                      onChange={handleScholarshipChange}
                                    />
                                  </div>

                                  <div className="col-lg-6 col-12">
                                    <label className="form-label mb-1 ms-3">
                                      Development Fee
                                    </label>
                                    <input
                                      type="number"
                                      className={`form-control text-dark ${
                                        errors?.scholarship?.developmentFee
                                          ? "border-danger"
                                          : ""
                                      }`}
                                      name="developmentFee"
                                      placeholder="..."
                                      value={
                                        formData.scholarship?.discount
                                          ?.developmentFee || ""
                                      }
                                      onChange={handleScholarshipChange}
                                    />
                                  </div>
                                </>
                              )}

                              <div className="col-lg-6 col-12 d-flex justify-content-end align-items-center">
                                <div className="d-flex flex-column ms-3">
                                  {Array.isArray(
                                    formData.scholarship?.siblingAdmissionNo,
                                  ) ? (
                                    formData.scholarship.siblingAdmissionNo.map(
                                      (no, index) => (
                                        <h5
                                          className="px-3 lh-sm m-0 text-dark rounded-pill d-flex justify-content-between"
                                          key={index}
                                        >
                                          {no}
                                          <i
                                            className="bi bi-x-circle-fill ms-3 text-secondary cursor-pointer"
                                            onClick={() =>
                                              removeSiblingAdmissionNo(index)
                                            }
                                          ></i>
                                        </h5>
                                      ),
                                    )
                                  ) : formData.scholarship
                                      ?.siblingAdmissionNo ? (
                                    <h5 className="px-3 lh-sm m-0 text-dark rounded-pill d-flex justify-content-between">
                                      {formData.scholarship.siblingAdmissionNo}
                                      <i
                                        className="bi bi-x-circle-fill ms-3 text-secondary cursor-pointer"
                                        onClick={() =>
                                          removeSiblingAdmissionNo(0)
                                        }
                                      ></i>
                                    </h5>
                                  ) : null}

                                  {staffId && (
                                    <h5 className="px-3 lh-sm m-0 text-dark rounded-pill d-flex justify-content-between">
                                      {staffId}
                                      <i
                                        className="bi bi-x-circle-fill ms-3 text-secondary cursor-pointer"
                                        onClick={() => setStaffId("")}
                                      ></i>
                                    </h5>
                                  )}
                                </div>
                              </div>

                              {formData.scholarship?.type && (
                                <div className="col-lg-6 col-12">
                                  <label class="form-label mb-1 ms-3">
                                    Scholarship From
                                  </label>
                                  <Select
                                    options={monthsList.map((monthsName) => ({
                                      label: monthsName,
                                      value: monthsName,
                                    }))}
                                    value={
                                      formData.scholarship?.scholarshipFrom
                                        ? {
                                            label:
                                              formData.scholarship
                                                .scholarshipFrom,
                                            value:
                                              formData.scholarship
                                                .scholarshipFrom,
                                          }
                                        : null
                                    }
                                    onChange={(selected) =>
                                      handleScholarshipChange({
                                        target: {
                                          name: "scholarshipFrom",
                                          value: selected?.value || "",
                                        },
                                      })
                                    }
                                    placeholder="Select Scholarship From"
                                    classNamePrefix="react-select"
                                    className={`text-dark ${
                                      errors?.scholarship?.scholarshipFrom
                                        ? "border-danger"
                                        : ""
                                    }`}
                                    isClearable
                                  />
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div class="px-5 py-3 mt-1 bg-white rounded-4">
                      <div
                        class="accordion accordion-flush"
                        id="accordionExample"
                      >
                        <div id="headingSeven">
                          <h3 class="m-0 fs-list">
                            <a
                              href="#"
                              class="d-flex align-items-center text-inherit"
                              data-bs-toggle="collapse"
                              data-bs-target="#collapseSeven"
                              aria-controls="collapseSeven"
                            >
                              <span class="me-auto fs-5">
                                7. Subjects{" "}
                                <span className="ms-3 text-secondary">
                                  ({countFilledFields(subjectSelections)}/
                                  {getTotalFields(subjectSelections)})
                                </span>
                              </span>
                              <div className="d-flex align-items-center">
                                <span className="badge me-5 px-3 bg-info-soft">
                                  Optional
                                </span>
                                <span className="collapse-toggle">
                                  <i class="bi bi-chevron-down"></i>
                                </span>
                              </div>
                            </a>
                          </h3>

                          <div
                            id="collapseSeven"
                            class="collapse"
                            aria-labelledby="headingSeven"
                            data-bs-parent="#accordionExample"
                          >
                            <div className="row g-2 mt-2 mb-3">
                              {subjectSelections.map((subjectValue, index) => (
                                <div className="col-lg-6 col-12" key={index}>
                                  <label class="form-label mb-1 ms-3">
                                    Subject {index + 1}
                                  </label>
                                  <Select
                                    options={subjectList.map((subject) => ({
                                      label: subject,
                                      value: subject,
                                    }))}
                                    value={
                                      subjectValue
                                        ? {
                                            label: subjectValue,
                                            value: subjectValue,
                                          }
                                        : null
                                    }
                                    onChange={(selected) =>
                                      handleSubjectSelectChange(
                                        index,
                                        selected,
                                      )
                                    }
                                    placeholder={`Select Subject ${
                                      index + 1
                                    }`}
                                    classNamePrefix="react-select"
                                    className="text-dark"
                                    isClearable
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="my-3 d-flex float-end me-5">
                      <button
                        type="submit"
                        disabled={loading || uploading}
                        class="btn btn-success mt-2 px-6"
                      >
                        {loading ? (
                          <div
                            class="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </form>
                </div>

                <div className="col-lg-4">
                  <div className="sticky-top top-10 z-1">
                    <div className="p-4 bg-white rounded-4 mb-2">
                      <div className="d-flex justify-content-center">
                        <div class="icon-shape icon-xxl p-7 border rounded-3 position-relative overflow-hidden me-2">
                          {preview ? (
                            <img
                              src={preview}
                              alt="Preview"
                              width="150"
                              height="150"
                              className="object-fit-cover rounded-3"
                            />
                          ) : (
                            <span className="position-absolute mt-2">
                              <i className="bi bi-image fs-2"></i>
                            </span>
                          )}

                          {uploading && (
                            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-dark bg-opacity-50 rounded-3">
                              <div
                                className="spinner-border text-light"
                                role="status"
                              ></div>
                            </div>
                          )}

                          <input
                            className="form-control border-0 opacity-0 position-absolute top-0 start-0 w-100 h-100"
                            type="file"
                            onChange={handleUploadStudentImage}
                            accept="image/*"
                            disabled={uploading}
                          />
                        </div>
                        <div class="icon-shape icon-xxl p-7 border rounded-3 position-relative">
                          <img
                            src="https://cdn-icons-png.flaticon.com/512/14021/14021384.png"
                            className="w-100 position-absolute p-2 rounded-4"
                            alt="QR"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="p-4 bg-white rounded-4 mb-2">
                      <div className="d-flex align-items-center justify-content-between mb-3">
                        <p className="m-0 fw-semibold text-dark lh-1">
                          Excel Tools
                          <br />
                          <span className="text-secondary fs-6 lh-sm fw-normal">
                            Bulk admission via spreadsheet
                          </span>
                        </p>
                        <span className="badge bg-info-soft px-3">
                          <i className="bi bi-file-earmark-spreadsheet me-1"></i>
                          XLSX
                        </span>
                      </div>

                      {/* Download Template */}
                      <div
                        className="rounded-4 p-3 mb-2 cursor-pointer"
                        style={{
                          background: "#f0fdf4",
                          border: "1.5px dashed #22c55e",
                        }}
                        onClick={handleDownloadTemplate}
                      >
                        <div className="d-flex align-items-center">
                          <div
                            className="rounded-3 d-flex align-items-center justify-content-center me-3 flex-shrink-0"
                            style={{
                              width: 42,
                              height: 42,
                              background: "#dcfce7",
                            }}
                          >
                            <i className="bi bi-file-earmark-arrow-down-fill text-success fs-5"></i>
                          </div>
                          <div>
                            <p className="m-0 fw-semibold text-dark lh-sm fs-5">
                              Download Template
                            </p>
                            <p className="m-0 text-secondary fs-6 lh-sm mt-1">
                              5 Sheets &nbsp;·&nbsp; Student, Parent, Address,
                              Transport, Instructions
                            </p>
                          </div>
                          <i className="bi bi-chevron-right text-secondary ms-auto"></i>
                        </div>
                      </div>

                      {/* Drag & Drop Upload */}
                      <div
                        className="rounded-4 p-3 text-center position-relative"
                        style={{
                          border: "1.5px dashed #6366f1",
                          background: "#f5f3ff",
                          cursor: "pointer",
                        }}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = "#ede9fe";
                        }}
                        onDragLeave={(e) => {
                          e.currentTarget.style.background = "#f5f3ff";
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          e.currentTarget.style.background = "#f5f3ff";
                          const file = e.dataTransfer.files?.[0];
                          if (
                            file &&
                            (file.name.endsWith(".xlsx") ||
                              file.name.endsWith(".xls"))
                          ) {
                            handleUploadExcel({
                              target: { files: [file], value: "" },
                            });
                          } else {
                            toast.error("Please drop a valid .xlsx file");
                          }
                        }}
                        onClick={() =>
                          document
                            .getElementById("upload-excel-input-right")
                            .click()
                        }
                      >
                        <input
                          id="upload-excel-input-right"
                          type="file"
                          accept=".xlsx,.xls"
                          className="d-none"
                          onChange={handleUploadExcel}
                        />
                        <div
                          className="rounded-3 d-flex align-items-center justify-content-center mx-auto mb-2"
                          style={{
                            width: 42,
                            height: 42,
                            background: "#ede9fe",
                          }}
                        >
                          <i className="bi bi-cloud-upload-fill text-primary fs-5"></i>
                        </div>
                        <p className="m-0 fw-semibold text-dark lh-sm fs-5 mt-1">
                          Drag & Drop Excel File
                        </p>
                        <p className="m-0 text-secondary fs-6 lh-sm">
                          or click to browse · .xlsx / .xls only
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </>
  );
}

export default AddStudent;